import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Settings as SettingsIcon, Save } from "lucide-react";

export default function Settings() {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    businessName: "Tiny Tax Lady",
    email: "info@tinytaxlady.com",
    phone: "",
    address: "",
    defaultTaxYear: new Date().getFullYear(),
    emailNotifications: true,
    smsNotifications: false,
    autoConfirmAppointments: false,
    cardBorderColor: "#e2e8f0",
    cardBackgroundColor: "#ffffff",
  });

  const handleSave = () => {
    // Save dashboard colors to localStorage
    localStorage.setItem('admin_dashboard_colors', JSON.stringify({
      cardBorderColor: settings.cardBorderColor,
      cardBackgroundColor: settings.cardBackgroundColor,
    }));
    
    // In a real implementation, this would save to a settings table
    toast({
      title: "Settings Saved",
      description: "Your preferences have been updated successfully.",
    });
  };

  const handleChange = (field: string, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <SettingsIcon className="h-8 w-8" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage your admin preferences and business configuration
          </p>
        </div>

        <div className="space-y-6">
          <Card className="admin-card">
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
              <CardDescription>
                Update your business details and contact information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  value={settings.businessName}
                  onChange={(e) => handleChange("businessName", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Business Address</Label>
                <Input
                  id="address"
                  value={settings.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder="123 Main St, City, State 12345"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardHeader>
              <CardTitle>Tax Configuration</CardTitle>
              <CardDescription>
                Set default values for tax processing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="defaultTaxYear">Default Tax Year</Label>
                <Input
                  id="defaultTaxYear"
                  type="number"
                  value={settings.defaultTaxYear}
                  onChange={(e) => handleChange("defaultTaxYear", parseInt(e.target.value))}
                  min="2020"
                  max="2030"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>
                Configure how you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="emailNotifications">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive notifications via email
                  </p>
                </div>
                <Switch
                  id="emailNotifications"
                  checked={settings.emailNotifications}
                  onCheckedChange={(checked) => handleChange("emailNotifications", checked)}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="smsNotifications">SMS Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive notifications via text message
                  </p>
                </div>
                <Switch
                  id="smsNotifications"
                  checked={settings.smsNotifications}
                  onCheckedChange={(checked) => handleChange("smsNotifications", checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardHeader>
              <CardTitle>Appointment Settings</CardTitle>
              <CardDescription>
                Configure appointment handling preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="autoConfirmAppointments">Auto-Confirm Appointments</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically confirm new appointment requests
                  </p>
                </div>
                <Switch
                  id="autoConfirmAppointments"
                  checked={settings.autoConfirmAppointments}
                  onCheckedChange={(checked) => handleChange("autoConfirmAppointments", checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardHeader>
              <CardTitle>Dashboard Customization</CardTitle>
              <CardDescription>
                Customize the appearance of dashboard cards
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cardBorderColor">Card Border Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    id="cardBorderColor"
                    type="color"
                    value={settings.cardBorderColor}
                    onChange={(e) => handleChange("cardBorderColor", e.target.value)}
                  />
                  <Input
                    type="text"
                    value={settings.cardBorderColor}
                    onChange={(e) => handleChange("cardBorderColor", e.target.value)}
                    className="flex-1"
                    placeholder="#e2e8f0"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cardBackgroundColor">Card Background Color</Label>
                <div className="flex items-center gap-3">
                  <input
                    id="cardBackgroundColor"
                    type="color"
                    value={settings.cardBackgroundColor}
                    onChange={(e) => handleChange("cardBackgroundColor", e.target.value)}
                  />
                  <Input
                    type="text"
                    value={settings.cardBackgroundColor}
                    onChange={(e) => handleChange("cardBackgroundColor", e.target.value)}
                    className="flex-1"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSave} size="lg">
              <Save className="mr-2 h-4 w-4" />
              Save Settings
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
